import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import { Empty } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import React, { FC, useEffect, useMemo, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { CsatRating } from 'api/reports/csat';
import { Collapse, PanelHeaderTitle } from 'components/atoms/collapse';
import { IconShell } from 'components/atoms/icon-shell';
import { Sticky } from 'components/molecules/sticky';
import { theme } from 'components/styles/theme';
import { queueMacrotask } from 'utils/macrotask';
import { GroupBy } from '../group-by';
import { getColumns } from './columns';
import { getGroupedData } from './get-grouped-data';
import { Header } from './header';
import { CSATScrollbar, Panel, Skeleton, Table } from './styles';

interface ListProps {
  data: CsatRating[];
  groupBy: GroupBy;
}

interface CsatListProps {
  groupBy: GroupBy;
  isLoading: boolean;
  data?: CsatRating[];
}

const List: FC<ListProps> = (props) => {
  const { data, groupBy: groupByKey } = props;
  const result = getGroupedData(groupByKey, data);
  const activeKeysResult = result.map((r) => r.key);
  const [activeKeys, setActiveKeys] = useState(activeKeysResult);

  useDeepCompareEffect(() => {
    setActiveKeys(activeKeysResult);
  }, [activeKeysResult]);
  const handleOnChange = (selectedKeys) => {
    setActiveKeys(selectedKeys);
  };

  const [sortInfo, setSortInfo] = useState<SorterResult<CsatRating>>({
    columnKey: GroupBy.ResponseDate,
    order: 'ascend',
  });

  // Listen and horizontally scroll all the tables rendered in page
  useEffect(() => {
    // Gets all the tables rendered in page
    const getTables = () => {
      return document.querySelectorAll('.ant-table-content');
    };

    const onScroll = (e) => {
      const source = e.target;

      getTables().forEach((table) => {
        table.scroll({ left: source.scrollLeft });
      });
    };

    // Adds Event listener as a macrotask
    queueMacrotask(() => {
      getTables().forEach((table) => {
        table.addEventListener('scroll', onScroll);
      });
    });

    // Cleanup
    return () => {
      getTables().forEach((table) => {
        table.removeEventListener('scroll', onScroll);
      });
    };
  }, [result]);

  const columnsWithLeftMostStuck = useMemo(() => {
    const newCols = getColumns(groupByKey, sortInfo);

    newCols[0].fixed = 'left';

    return newCols;
  }, [groupByKey, sortInfo]);

  return (
    <>
      <Sticky top={150} layer={theme.filters.layer - 1}>
        <Header columns={columnsWithLeftMostStuck} onChange={setSortInfo} />
      </Sticky>
      <Collapse
        key="csat-list"
        ghost
        defaultActiveKey={activeKeys}
        activeKey={activeKeys}
        onChange={handleOnChange}
        expandIconPosition="right"
        expandIcon={({ isActive }) => {
          return isActive ? <CaretUp16 /> : <CaretDown16 />;
        }}
      >
        {result.map((item) => (
          <Panel
            key={item.key}
            showArrow
            header={
              <PanelHeaderTitle
                title={item.headerData.title}
                icon={
                  item.headerData.icon && (
                    <IconShell icon={item.headerData.icon} />
                  )
                }
              />
            }
          >
            <Table<any>
              size="small"
              dataSource={item.result}
              columns={columnsWithLeftMostStuck}
              pagination={false}
              showHeader={false}
              tableLayout="fixed"
            />
          </Panel>
        ))}

        <CSATScrollbar className="ant-table-content">
          <div
            style={{
              width: getColumns(groupByKey, sortInfo).length * 400,
              height: '40px',
            }}
          />
        </CSATScrollbar>
      </Collapse>
    </>
  );
};

export const CsatList: FC<CsatListProps> = (props) => {
  const { groupBy, isLoading, data = [] } = props;

  if (isLoading) {
    return <Skeleton active loading={isLoading} />;
  }

  if (data.length > 0) {
    return <List data={data} groupBy={groupBy} />;
  }

  return <Empty />;
};
